import { en } from "./locales/en";
import { zhHK } from "./locales/zh-HK";
import { zhTW } from "./locales/zh-TW";
import { zhCN } from "./locales/zh-CN";
import { ja } from "./locales/ja";
import { ko } from "./locales/ko";
import { th } from "./locales/th";
import { ru } from "./locales/ru";

export type Translations = typeof en;
export type Locale = "en" | "zh-HK" | "zh-TW" | "zh-CN" | "ja" | "ko" | "th" | "ru";

export const LOCALES: Locale[] = ["en", "zh-HK", "zh-TW", "zh-CN", "ja", "ko", "th", "ru"];
export const DEFAULT_LOCALE: Locale = "en";

export const LOCALE_COOKIE = "caratzon_lang";

export const translations: Record<Locale, Translations> = {
  en,
  "zh-HK": zhHK,
  "zh-TW": zhTW,
  "zh-CN": zhCN,
  ja,
  ko,
  th,
  ru,
};

// ─── Server-side language detection ─────────────────────────────────────────
// Priority: cookie → Accept-Language header → default

export function detectLocale(
  cookieHeader: string | null,
  acceptLanguageHeader: string | null,
): Locale {
  // 1. Cookie takes highest priority
  if (cookieHeader) {
    const match = cookieHeader.match(/caratzon_lang=([^;]+)/);
    const fromCookie = match?.[1]?.trim() as Locale | undefined;
    if (fromCookie && LOCALES.includes(fromCookie)) return fromCookie;
  }

  // 2. Parse Accept-Language header
  if (acceptLanguageHeader) {
    const langs = acceptLanguageHeader
      .split(",")
      .map((entry) => {
        const [lang, q] = entry.trim().split(";q=");
        return { lang: lang.trim().toLowerCase(), q: q ? parseFloat(q) : 1 };
      })
      .sort((a, b) => b.q - a.q);

    for (const { lang } of langs) {
      if (lang === "ja" || lang.startsWith("ja-")) return "ja";
      if (lang === "zh-hk" || lang === "zh-yue" || lang === "zh-hant-hk") return "zh-HK";
      if (lang === "zh-tw" || lang === "zh-hant-tw") return "zh-TW";
      if (lang === "zh-hant") return "zh-TW"; // default Traditional Chinese → Taiwan
      if (lang === "zh-cn" || lang === "zh-hans" || lang === "zh") return "zh-CN";
      if (lang === "ko" || lang.startsWith("ko-")) return "ko";
      if (lang === "th" || lang.startsWith("th-")) return "th";
      if (lang === "ru" || lang.startsWith("ru-")) return "ru";
      if (lang.startsWith("en")) return "en";
    }
  }

  return DEFAULT_LOCALE;
}

// ─── Cookie builder for language switcher action ─────────────────────────────

export function buildLocaleCookie(locale: Locale): string {
  return `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}
